import { makeAutoObservable, runInAction } from 'mobx';

interface Currency {
  code: string;
  name: string;
  symbol: string;
  rate: number; // Rate relative to USD
}

interface ConversionHistory {
  id: string;
  fromAmount: number;
  fromCurrency: Currency;
  toAmount: number;
  toCurrency: Currency;
  timestamp: number;
  rate: number;
}

class CurrencyConverterStore {
  private _currencies: Currency[] = [];
  private _fromCurrencyCode: string = 'USD';
  private _toCurrencyCode: string = 'EUR';
  private _inputAmount: string = '1';
  private _result: number = 0;
  private _history: ConversionHistory[] = [];
  private _isLoading: boolean = false;
  private _lastUpdated: number = 0;

  constructor() {
    makeAutoObservable(this);
    this.initializeCurrencies();
    this.loadHistory();
    this.performConversion();
  }

  get currencies(): Currency[] {
    return this._currencies;
  }

  get fromCurrency(): Currency | null {
    return this._currencies.find(c => c.code === this._fromCurrencyCode) || null;
  }

  get toCurrency(): Currency | null {
    return this._currencies.find(c => c.code === this._toCurrencyCode) || null;
  }

  get inputAmount(): string {
    return this._inputAmount;
  }

  get result(): number {
    return this._result;
  }

  get formattedResult(): string {
    if (this._result === 0) return '0.00';
    return this._result.toFixed(2);
  }

  get history(): ConversionHistory[] {
    return this._history.slice().reverse(); // Most recent first
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  get lastUpdated(): Date | null {
    return this._lastUpdated ? new Date(this._lastUpdated) : null;
  }

  get exchangeRate(): number {
    if (!this.fromCurrency || !this.toCurrency) return 0;
    // Convert from source currency to USD, then USD to target currency
    return (1 / this.fromCurrency.rate) * this.toCurrency.rate;
  }

  setFromCurrency(currencyCode: string): void {
    this._fromCurrencyCode = currencyCode;
    this.performConversion();
  }

  setToCurrency(currencyCode: string): void {
    this._toCurrencyCode = currencyCode;
    this.performConversion();
  }

  setInputAmount(amount: string): void {
    this._inputAmount = amount;
    this.performConversion();
  }

  swapCurrencies(): void {
    const tempFrom = this._fromCurrencyCode;
    const tempTo = this._toCurrencyCode;
    
    this._fromCurrencyCode = tempTo;
    this._toCurrencyCode = tempFrom;
    this._inputAmount = this.formattedResult;
    
    this.performConversion();
  }

  clearHistory(): void {
    this._history = [];
    this.saveHistory();
  }

  deleteHistoryItem(id: string): void {
    this._history = this._history.filter(item => item.id !== id);
    this.saveHistory();
  }

  useHistoryConversion(historyItem: ConversionHistory): void {
    this._fromCurrencyCode = historyItem.fromCurrency.code;
    this._toCurrencyCode = historyItem.toCurrency.code;
    this._inputAmount = historyItem.fromAmount.toString();
    this.performConversion();
  }

  // Simulate updating exchange rates (in a real app, this would fetch from an API)
  async updateExchangeRates(): Promise<void> {
    runInAction(() => {
      this._isLoading = true;
    });

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Simulate rate fluctuations (±2% change)
      runInAction(() => {
        this._currencies = this._currencies.map(currency => {
          if (currency.code === 'USD') return currency; // USD is base
          
          const fluctuation = (Math.random() - 0.5) * 0.04; // ±2%
          const newRate = currency.rate * (1 + fluctuation);
          
          return {
            ...currency,
            rate: Math.max(0.001, newRate) // Ensure rate doesn't go negative
          };
        });
        
        this._lastUpdated = Date.now();
      });
      
      this.performConversion();
    } catch (error) {
      console.error('Failed to update exchange rates:', error);
    } finally {
      runInAction(() => {
        this._isLoading = false;
      });
    }
  }

  private performConversion(): void {
    const amount = parseFloat(this._inputAmount);
    
    if (isNaN(amount) || !this.fromCurrency || !this.toCurrency) {
      this._result = 0;
      return;
    }

    const rate = this.exchangeRate;
    const result = amount * rate;
    
    runInAction(() => {
      this._result = result;
    });

    // Add to history if conversion is valid and not zero
    if (amount !== 0 && this.fromCurrency && this.toCurrency) {
      this.addToHistory(amount, this.fromCurrency, result, this.toCurrency, rate);
    }
  }

  private addToHistory(fromAmount: number, fromCurrency: Currency, toAmount: number, toCurrency: Currency, rate: number): void {
    // Don't add duplicate consecutive conversions
    const lastItem = this._history[this._history.length - 1];
    if (lastItem && 
        lastItem.fromAmount === fromAmount && 
        lastItem.fromCurrency.code === fromCurrency.code && 
        lastItem.toCurrency.code === toCurrency.code) {
      return;
    }

    const historyItem: ConversionHistory = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      fromAmount,
      fromCurrency: { ...fromCurrency },
      toAmount,
      toCurrency: { ...toCurrency },
      timestamp: Date.now(),
      rate
    };

    this._history.push(historyItem);
    
    // Keep only last 50 items
    if (this._history.length > 50) {
      this._history = this._history.slice(-50);
    }

    this.saveHistory();
  }

  private initializeCurrencies(): void {
    // Static exchange rates (in a real app, these would come from an API)
    this._currencies = [
      { code: 'USD', name: 'US Dollar', symbol: '$', rate: 1.0 }, // Base currency
      { code: 'EUR', name: 'Euro', symbol: '€', rate: 0.85 },
      { code: 'GBP', name: 'British Pound', symbol: '£', rate: 0.73 },
      { code: 'JPY', name: 'Japanese Yen', symbol: '¥', rate: 110.0 },
      { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', rate: 1.35 },
      { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', rate: 1.25 },
      { code: 'CHF', name: 'Swiss Franc', symbol: 'Fr', rate: 0.92 },
      { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', rate: 6.45 },
      { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', rate: 8.60 },
      { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$', rate: 1.43 },
      { code: 'MXN', name: 'Mexican Peso', symbol: '$', rate: 17.0 },
      { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', rate: 1.35 },
      { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$', rate: 7.80 },
      { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', rate: 8.25 },
      { code: 'INR', name: 'Indian Rupee', symbol: '₹', rate: 74.5 },
      { code: 'KRW', name: 'South Korean Won', symbol: '₩', rate: 1180.0 },
      { code: 'RUB', name: 'Russian Ruble', symbol: '₽', rate: 73.5 },
      { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', rate: 5.20 },
      { code: 'ZAR', name: 'South African Rand', symbol: 'R', rate: 14.8 },
      { code: 'TRY', name: 'Turkish Lira', symbol: '₺', rate: 8.30 }
    ];
    
    this._lastUpdated = Date.now();
  }

  private saveHistory(): void {
    try {
      localStorage.setItem('general-tools-currency-converter-history', JSON.stringify(this._history));
    } catch (error) {
      console.error('Failed to save currency converter history:', error);
    }
  }

  private loadHistory(): void {
    try {
      const saved = localStorage.getItem('general-tools-currency-converter-history');
      if (saved) {
        this._history = JSON.parse(saved);
      }
    } catch (error) {
      console.error('Failed to load currency converter history:', error);
      this._history = [];
    }
  }

  // Popular currency pairs for quick access
  get popularPairs(): Array<{from: string, to: string, label: string}> {
    return [
      { from: 'USD', to: 'EUR', label: 'USD → EUR' },
      { from: 'EUR', to: 'USD', label: 'EUR → USD' },
      { from: 'USD', to: 'GBP', label: 'USD → GBP' },
      { from: 'GBP', to: 'USD', label: 'GBP → USD' },
      { from: 'USD', to: 'JPY', label: 'USD → JPY' },
      { from: 'EUR', to: 'GBP', label: 'EUR → GBP' },
      { from: 'USD', to: 'CAD', label: 'USD → CAD' },
      { from: 'USD', to: 'AUD', label: 'USD → AUD' }
    ];
  }

  setPopularPair(fromCode: string, toCode: string): void {
    this._fromCurrencyCode = fromCode;
    this._toCurrencyCode = toCode;
    this.performConversion();
  }
}

export const currencyConverterStore = new CurrencyConverterStore();
